import React from 'react';
import { Form, Upload } from 'antd';
import { isFeatureEnabled } from '../../../company/session';
import { FEATURES } from '../../../company/features';

// Turns an AuditProduct doc into a searchable Select option: "Nombre — 7591234 — 500g".
const barcodeToOption = (b) => ({
    value: b?._id,
    label: [b?.name, b?.code, b?.grams ? `${b.grams}g` : null].filter(Boolean).join(' — ')
});

/** Form, barcode lookup and image upload for the create / edit product drawer. */
export function useProductDrawer({ isOpen, selectedProduct, onSubmit, onUploadImage, searchBarcodes }) {
    const [form] = Form.useForm();
    const [uploading, setUploading] = React.useState(false);
    const [uploadError, setUploadError] = React.useState('');
    const [barcodeOptions, setBarcodeOptions] = React.useState([]);
    const [barcodeSearching, setBarcodeSearching] = React.useState(false);
    const searchTimer = React.useRef(null);

    React.useEffect(() => {
        if (isOpen) {
            const linked = selectedProduct?.auditProductId;
            const linkedId = linked && typeof linked === 'object' ? linked._id : linked || undefined;
            form.setFieldsValue({
                name: selectedProduct?.name || '',
                value: selectedProduct?.value || '',
                category: selectedProduct?.category || '',
                auditProductId: linkedId,
                imageUri: selectedProduct?.images?.uri || '',
                imageThumbnail: selectedProduct?.images?.thumbnail || '',
                active: selectedProduct?.active ?? true
            });
            // Seed the options with the currently linked barcode so its label renders
            // before the user searches for anything.
            setBarcodeOptions(linked && typeof linked === 'object' ? [barcodeToOption(linked)] : []);
        } else {
            form.resetFields();
            setUploadError('');
            setBarcodeOptions([]);
        }
    }, [isOpen, selectedProduct, form]);

    // Debounced async search against the auditor barcode catalog.
    const handleBarcodeSearch = (query) => {
        if (searchTimer.current) clearTimeout(searchTimer.current);
        const term = String(query || '').trim();
        if (term.length < 2 || typeof searchBarcodes !== 'function') return;
        searchTimer.current = setTimeout(async () => {
            try {
                setBarcodeSearching(true);
                const results = await searchBarcodes(term);
                setBarcodeOptions((results || []).map(barcodeToOption).filter((o) => o.value));
            } catch (error) {
                setBarcodeOptions([]);
            } finally {
                setBarcodeSearching(false);
            }
        }, 350);
    };

    const submit = async () => {
        const values = await form.validateFields();
        await onSubmit({
            name: values.name,
            value: values.value,
            category: values.category,
            // The audit-barcode link only exists on backends with auditors.
            ...(isFeatureEnabled(FEATURES.AUDITOR) ? { auditProductId: values.auditProductId || null } : {}),
            images: {
                uri: values.imageUri || '',
                thumbnail: values.imageThumbnail || ''
            },
            active: values.active
        });
    };

    const beforeUpload = (file) => {
        const isValidType = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type);
        if (!isValidType) {
            setUploadError('Formato inválido. Usa JPG, PNG o WEBP.');
            return Upload.LIST_IGNORE;
        }

        const isValidSize = file.size <= 5 * 1024 * 1024;
        if (!isValidSize) {
            setUploadError('La imagen no debe superar 5MB.');
            return Upload.LIST_IGNORE;
        }

        setUploadError('');
        return true;
    };

    const customUpload = async ({ file, onSuccess, onError }) => {
        try {
            setUploading(true);
            const uploaded = await onUploadImage(file);
            if (!uploaded?.uri) throw new Error('No se recibió URL de imagen');

            form.setFieldsValue({
                imageUri: uploaded.uri,
                imageThumbnail: uploaded.thumbnail || ''
            });
            setUploadError('');
            onSuccess?.(uploaded);
        } catch (error) {
            setUploadError(error?.response?.data?.error || error?.message || 'No se pudo subir la imagen');
            onError?.(error);
        } finally {
            setUploading(false);
        }
    };

    const imageUri = Form.useWatch('imageUri', form);

    return {
        form,
        uploading,
        uploadError,
        barcodeOptions,
        barcodeSearching,
        handleBarcodeSearch,
        submit,
        beforeUpload,
        customUpload,
        imageUri
    };
}
